import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { getSignedUrls } from './storage';
import { updateMessage, deleteMessage } from './api';
import MessageEditor, { type MessageEditorHandle } from './MessageEditor';
import AttachmentCard from './AttachmentCard';
import { useDialog } from '../ui/Dialogs';
import { ReactionChips, ReactionAddButton } from './ReactionBar';
import ReminderFromNote from './ReminderFromNote';
import type { Message } from './types';

const ALLOWED_TAGS = ['p', 'br', 'strong', 'em', 'u', 's', 'a', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'img', 'span'];
const ALLOWED_ATTR = ['href', 'target', 'rel', 'src', 'alt', 'data-storage-path', 'data-mention-id', 'class', 'style'];

function collectStoragePaths(html: string | null | undefined): string[] {
  if (!html) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return [...doc.querySelectorAll('img[data-storage-path]')]
    .map((img) => img.getAttribute('data-storage-path'))
    .filter((p): p is string => p != null);
}

function replaceImageSrcs(html: string | null | undefined, urlMap: Record<string, string>): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('img[data-storage-path]').forEach((img) => {
    const path = img.getAttribute('data-storage-path');
    if (path && urlMap[path]) img.setAttribute('src', urlMap[path] as string);
  });
  return doc.body.innerHTML;
}

function relativeDate(input: string | null | undefined): string {
  if (!input) return '';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'ahora';
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}-${mm}-${yy}`;
}

function fullDateTime(input: string | null | undefined): string {
  if (!input) return '';
  const d = new Date(input);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

// Acuse de lectura de una respuesta propia: a quién va dirigida (asignado +
// seguidores, sin su autor) repartido entre quién ya la ha visto y quién no.
export interface ReaderRow {
  id: string;
  nombre: string;
  color: string;
  at?: string; // last_read_at de quien ya la vio
}
export interface MessageReadBy {
  readers: ReaderRow[];
  pending: ReaderRow[];
}

// "Ojito" estilo acuse de lectura: gris claro si nadie ha visto la nota, gris
// oscuro (y pupila rellena) en cuanto alguien la ve. Al pasar el ratón se
// despliega quién la ha visto —con la hora— y quién la tiene pendiente.
function ReadEye({ readers, pending }: MessageReadBy) {
  const [open, setOpen] = useState(false);
  const seen = readers.length > 0;
  return (
    <span
      style={eyeStyles.wrap}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <svg
        width="17" height="17" viewBox="0 0 24 24" fill="none"
        stroke={seen ? '#475569' : '#cbd5e1'} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}
      >
        <path d="M1.5 12s3.5-7 10.5-7 10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12z" />
        <circle cx="12" cy="12" r="3" fill={seen ? '#475569' : 'none'} />
      </svg>
      {open && (
        <div style={eyeStyles.pop}>
          <div style={eyeStyles.title}>
            {seen ? `Visto por ${readers.length}` : 'Nadie lo ha visto aún'}
          </div>
          {readers.map((r) => (
            <div key={r.id} style={eyeStyles.popRow}>
              <span style={{ ...eyeStyles.mono, background: r.color }}>
                {(r.nombre.charAt(0) || '?').toUpperCase()}
              </span>
              <span style={eyeStyles.popName}>{r.nombre}</span>
              <span style={eyeStyles.popTime}>{r.at ? fullDateTime(r.at) : ''}</span>
            </div>
          ))}
          {pending.length > 0 && (
            <>
              <div style={eyeStyles.subtitle}>Pendiente</div>
              {pending.map((r) => (
                <div key={r.id} style={{ ...eyeStyles.popRow, opacity: 0.5 }}>
                  <span style={{ ...eyeStyles.mono, background: r.color }}>
                    {(r.nombre.charAt(0) || '?').toUpperCase()}
                  </span>
                  <span style={eyeStyles.popName}>{r.nombre}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </span>
  );
}

interface Props {
  message: Message;
  isMine: boolean;
  currentUserId: string | undefined;
  nameById?: Map<string, string>;
  // Acuse de lectura de la nota (solo en mensajes propios con audiencia).
  readBy?: MessageReadBy;
  markedUnread?: boolean;
  onMarkUnreadFrom?: (messageCreatedAt: string) => void;
  // Resaltado temporal al llegar por enlace directo (/tareas/{id}#msg-{msgId}).
  highlighted?: boolean;
  // El usuario actual tiene un recordatorio pendiente sobre esta nota.
  hasReminder?: boolean;
  // Id de ese recordatorio (el de fecha más próxima); para abrirlo al pulsarlo.
  reminderId?: string;
  // Aviso para refrescar el indicador tras crear un recordatorio.
  onReminderCreated?: () => void;
}

export default function MessageBubble({
  message,
  isMine,
  currentUserId,
  nameById,
  readBy,
  markedUnread = false,
  onMarkUnreadFrom,
  highlighted = false,
  hasReminder = false,
  reminderId,
  onReminderCreated,
}: Props) {
  const navigate = useNavigate();
  const color = message.author?.color || '#64748b';
  const nombre = message.author?.nombre || 'Desconocido';
  const initial = (nombre.charAt(0) || '?').toUpperCase();

  const inlinePaths = useMemo(() => collectStoragePaths(message.body), [message.body]);
  const [urlMap, setUrlMap] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [reminderOpen, setReminderOpen] = useState(false);
  // Menú "···" con las acciones secundarias (recordatorio, editar, eliminar,
  // marcar no leída): mantiene la fila limpia (solo ojo + emoji visibles).
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLSpanElement>(null);
  const editorRef = useRef<MessageEditorHandle>(null);
  const { confirm, alert } = useDialog();

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  useEffect(() => {
    let cancelled = false;
    if (inlinePaths.length === 0) {
      setUrlMap({});
      return undefined;
    }
    getSignedUrls(inlinePaths).then((map) => {
      if (!cancelled) setUrlMap(map);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [inlinePaths]);

  const safeHtml = useMemo(() => {
    const replaced = replaceImageSrcs(message.body || '', urlMap);
    return DOMPurify.sanitize(replaced, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ADD_ATTR: ['target'],
    });
  }, [message.body, urlMap]);

  const handleSaveEdit = async () => {
    const editor = editorRef.current;
    if (!editor || editor.isEmpty()) return;
    const { body } = editor.getContent();
    setSaving(true);
    try {
      await updateMessage(message.id, { body });
      setEditing(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      alert({ title: 'No se pudo guardar', message: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    confirm({
      title: 'Eliminar mensaje',
      message: 'Se borrará este mensaje junto con sus imágenes y adjuntos. No se puede deshacer.',
      confirmLabel: 'Eliminar',
      busyLabel: 'Eliminando…',
      danger: true,
      // La fila desaparece de la lista vía realtime (evento DELETE).
      onConfirm: () => deleteMessage(message.id),
    });
  };

  // Abre la imagen a tamaño completo al pincharla dentro del cuerpo del mensaje.
  const handleBodyClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG') {
      const src = (target as HTMLImageElement).currentSrc || (target as HTMLImageElement).src;
      if (src) setLightboxSrc(src);
    }
  };

  return (
    <div
      id={`msg-${message.id}`}
      className="msg-row"
      style={{
        ...styles.row,
        ...(message.is_private ? styles.rowPrivate : null),
        ...(highlighted ? styles.rowHighlight : null),
      }}
    >
      <div style={{ ...styles.avatar, background: color }}>{initial}</div>
      <div style={styles.content}>
        <div style={styles.meta}>
          <span style={styles.authorName}>{nombre}</span>
          {/* Solo llega al cliente si es del propio usuario (RLS). */}
          {message.is_private && (
            <span style={styles.privateBadge} title="Nota privada: solo la ves tú">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="4" y="11" width="16" height="10" rx="2" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
              Privada
            </span>
          )}
          <span style={styles.date} title={fullDateTime(message.created_at)}>
            {relativeDate(message.created_at)}
          </span>
          {message.edited_at && (
            <span style={styles.edited} title={`Editado: ${fullDateTime(message.edited_at)}`}>
              (editado)
            </span>
          )}
          <div style={styles.metaRight}>
            {isMine && !editing && readBy && (readBy.readers.length + readBy.pending.length) > 0 && (
              <ReadEye readers={readBy.readers} pending={readBy.pending} />
            )}
            {!editing && (
              <ReactionAddButton
                messageId={message.id}
                reactions={message.reactions ?? []}
                currentUserId={currentUserId}
              />
            )}
            {/* Reloj ENCENDIDO: solo cuando ya tienes un recordatorio en esta nota.
                Es un estado de un vistazo (+ atajo a la Agenda), no una acción más;
                "Crear recordatorio" vive en el menú "···". */}
            {!editing && hasReminder && reminderId && (
              <button
                type="button"
                onClick={() => navigate(`/agenda?recordatorio=${reminderId}`)}
                title="Ver tu recordatorio sobre esta nota"
                style={{ ...styles.iconBtn, ...styles.reminderActive }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="13" r="8" /><path d="M12 9v4l2 2" stroke="#fff" fill="none" /><path d="M5 3 2 6" fill="none" /><path d="m22 6-3-3" fill="none" />
                </svg>
              </button>
            )}
            {!editing && currentUserId && (
              <span ref={menuRef} style={styles.menuWrap}>
                <button
                  type="button"
                  onClick={() => setMenuOpen((v) => !v)}
                  title="Más acciones"
                  aria-label="Más acciones"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  style={{ ...styles.iconBtn, ...(menuOpen ? styles.iconBtnActive : null) }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" />
                  </svg>
                </button>
                {menuOpen && (
                  <div style={styles.menu} role="menu">
                    {!hasReminder && (
                      <button
                        type="button"
                        className="kebab-item"
                        style={styles.menuItem}
                        onClick={() => { setMenuOpen(false); setReminderOpen(true); }}
                      >
                        <span style={styles.menuIcon}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="13" r="8" /><path d="M12 9v4l2 2" /><path d="M5 3 2 6" /><path d="m22 6-3-3" />
                          </svg>
                        </span>
                        Crear recordatorio
                      </button>
                    )}
                    {isMine && (
                      <button
                        type="button"
                        className="kebab-item"
                        style={styles.menuItem}
                        onClick={() => { setMenuOpen(false); setEditing(true); }}
                      >
                        <span style={styles.menuIcon}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
                          </svg>
                        </span>
                        Editar
                      </button>
                    )}
                    {!isMine && onMarkUnreadFrom && (
                      <button
                        type="button"
                        className="kebab-item"
                        style={styles.menuItem}
                        onClick={() => { setMenuOpen(false); onMarkUnreadFrom(message.created_at); }}
                      >
                        <span style={{ ...styles.menuIcon, color: markedUnread ? '#1d4ed8' : '#94a3b8' }}>●</span>
                        {markedUnread ? 'Marcar como leída' : 'Marcar como no leída desde aquí'}
                      </button>
                    )}
                    {isMine && (
                      <button
                        type="button"
                        className="kebab-item"
                        style={{ ...styles.menuItem, color: '#dc2626' }}
                        onClick={() => { setMenuOpen(false); handleDelete(); }}
                      >
                        <span style={{ ...styles.menuIcon, color: '#dc2626' }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1L5 6" />
                          </svg>
                        </span>
                        Eliminar
                      </button>
                    )}
                  </div>
                )}
              </span>
            )}
          </div>
        </div>

        {editing ? (
          <div style={{ marginTop: 4 }}>
            <MessageEditor
              ref={editorRef}
              taskId={message.task_id}
              initialContent={replaceImageSrcs(message.body || '', urlMap)}
              showSendButton={false}
              minHeight={80}
              onSend={() => {}}
            />
            <div style={styles.editActions}>
              <button type="button" onClick={() => setEditing(false)} style={styles.btnGhost}>
                Cancelar
              </button>
              <button type="button" onClick={handleSaveEdit} disabled={saving} style={styles.btnPrimary}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div
              className="msg-body"
              style={styles.body}
              onClick={handleBodyClick}
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
            {message.attachments && message.attachments.length > 0 && (
              <div style={styles.attachments}>
                {message.attachments.map((att) => (
                  <AttachmentCard key={att.id} attachment={att} />
                ))}
              </div>
            )}
            <ReactionChips
              messageId={message.id}
              reactions={message.reactions ?? []}
              currentUserId={currentUserId}
              nameById={nameById}
            />
          </>
        )}
      </div>

      {lightboxSrc && (
        <div style={styles.lightbox} onClick={() => setLightboxSrc(null)}>
          <img src={lightboxSrc} alt="" style={styles.lightboxImg} />
        </div>
      )}

      {reminderOpen && currentUserId && (
        <ReminderFromNote
          message={message}
          userId={currentUserId}
          onClose={() => setReminderOpen(false)}
          onCreated={onReminderCreated}
        />
      )}
    </div>
  );
}

// Ojito de acuse de lectura + su popover "quién lo ha visto".
const eyeStyles: Record<string, CSSProperties> = {
  wrap: { position: 'relative', display: 'inline-flex', alignItems: 'center', cursor: 'default', padding: '0 2px' },
  pop: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    right: 0,
    zIndex: 50,
    minWidth: 200,
    maxWidth: 280,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    boxShadow: '0 12px 28px rgba(15,23,42,0.16)',
    padding: '8px 6px',
    cursor: 'default',
  },
  title: {
    fontSize: 11,
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    padding: '2px 8px 6px',
  },
  subtitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#cbd5e1',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    padding: '6px 8px 4px',
    marginTop: 2,
    borderTop: '1px solid #f1f5f9',
  },
  popRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px' },
  mono: {
    width: 18,
    height: 18,
    minWidth: 18,
    borderRadius: 9,
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  popName: { fontSize: 13.5, color: '#334155', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  popTime: { marginLeft: 'auto', fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap' },
};

const styles: Record<string, CSSProperties> = {
  row: {
    display: 'flex',
    gap: 14,
    padding: '18px 0',
    borderBottom: '1px solid #f1f5f9',
    alignItems: 'flex-start',
    transition: 'background 0.5s ease, box-shadow 0.5s ease',
    scrollMarginTop: 80,
  },
  // Nota privada del propio usuario: fondo ámbar tenue y borde punteado para
  // que se distinga de un vistazo de lo que ve el resto del hilo.
  rowPrivate: {
    background: '#fffdf5',
    border: '1px dashed #fcd34d',
    borderRadius: 10,
    padding: '14px 14px',
    margin: '6px 0',
  },
  privateBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 7px',
    borderRadius: 999,
    background: '#fef3c7',
    color: '#b45309',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.2,
    alignSelf: 'center',
  },
  // Flash temporal al llegar desde el enlace de un recordatorio.
  rowHighlight: {
    background: '#fffbeb',
    boxShadow: 'inset 3px 0 0 #f59e0b',
    borderRadius: 8,
  },
  avatar: {
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: 18,
    color: '#fff',
    fontWeight: 700,
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  meta: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    flexWrap: 'wrap',
  },
  authorName: {
    fontSize: 14,
    fontWeight: 700,
    color: '#0f172a',
  },
  date: {
    fontSize: 12,
    color: '#94a3b8',
  },
  edited: {
    fontSize: 12,
    color: '#94a3b8',
    fontStyle: 'italic',
  },
  metaRight: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  iconBtn: {
    background: 'transparent',
    border: 'none',
    color: '#94a3b8',
    cursor: 'pointer',
    padding: 4,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconBtnActive: {
    background: '#eff5ff',
    color: '#1d4ed8',
  },
  // Reloj "encendido": tengo un recordatorio pendiente sobre esta nota.
  reminderActive: {
    color: '#7c3aed',
  },
  // Menú "···" de acciones secundarias de la nota.
  menuWrap: { position: 'relative', display: 'inline-flex' },
  menu: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    right: 0,
    zIndex: 50,
    minWidth: 210,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    boxShadow: '0 12px 28px rgba(15,23,42,0.16)',
    padding: 6,
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 11,
    width: '100%',
    padding: '9px 10px',
    borderRadius: 7,
    fontSize: 14,
    color: '#0f172a',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },
  menuIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    color: '#64748b',
    fontSize: 12,
  },
  body: {
    marginTop: 4,
    fontSize: 14,
    lineHeight: 1.6,
    color: '#1e293b',
    wordBreak: 'break-word',
  },
  attachments: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 10,
    color: '#334155',
  },
  editActions: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
    marginTop: 6,
  },
  btnGhost: {
    background: 'transparent',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    padding: '6px 14px',
    cursor: 'pointer',
    color: '#475569',
    fontSize: 13,
    fontWeight: 600,
  },
  btnPrimary: {
    background: '#0f172a',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '7px 16px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
  },
  lightbox: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    zIndex: 1000,
    cursor: 'zoom-out',
  },
  lightboxImg: {
    maxWidth: '100%',
    maxHeight: '100%',
    borderRadius: 8,
    boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
  },
};
